import { HttpError, paginateLocally } from "@nasam/kernel";

import {
  DEFAULT_GEOFENCE_COLOR,
  GEOFENCE_BULK_LIMITS,
  GEOFENCE_RADIUS_MAX,
  GEOFENCE_RADIUS_MIN,
  type Geofence,
  type GeofenceBulkRow,
  type GeofenceColor,
  type GeofenceShape,
  type GeofencesRepository,
} from "../domain";

export type MockGeofenceSheetRow = Omit<GeofenceBulkRow, "status" | "errors">;

export interface MockGeofencesRepositoryOptions {
  readonly geofences?: readonly Geofence[];
  /** What any uploaded `.xlsx` reads as: the mock can't open a workbook. */
  readonly bulkSheet?: readonly MockGeofenceSheetRow[];
  /** The columns any uploaded `.xlsx` reads as having. */
  readonly bulkColumns?: readonly string[];
  /** Simulated network latency, so the loading states show in the app. */
  readonly latencyMs?: number;
}

/** Search by part of the name, any case, as the POI API does. */
function matching(
  geofences: readonly Geofence[],
  search: string | undefined,
): Geofence[] {
  const needle = search?.trim().toLocaleLowerCase() ?? "";
  return geofences.filter((geofence) =>
    geofence.name.toLocaleLowerCase().includes(needle),
  );
}

function notFound(id: string, method: string): HttpError {
  return new HttpError("Geofence not found.", {
    status: 404,
    code: undefined,
    method,
    url: `/geofences/${id}`,
    data: {
      statusCode: 404,
      path: `/api/v1/geofences/${id}`,
      message: "Geofence not found.",
    },
  });
}

function conflict(method: string): HttpError {
  const message = "A Geofence with this name already exists.";
  return new HttpError(message, {
    status: 409,
    code: undefined,
    method,
    url: "/geofences",
    data: { statusCode: 409, path: "/api/v1/geofences", message },
  });
}

function refusal(status: number, message: string): HttpError {
  return new HttpError(message, {
    status,
    code: undefined,
    method: "POST",
    url: "/geofences/bulk-upload/preview",
    data: { statusCode: status, path: "/api/v1/geofences/bulk-upload/preview", message },
  });
}

const BULK_COLUMNS = ["name", "lat", "lng", "radius"] as const;
const SPREADSHEET_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** What the API refuses a whole file for (Scenarios 5, 6, 15, 16, 17). */
function bulkFileRefusal(
  file: File,
  columns: readonly string[],
  sheet: readonly MockGeofenceSheetRow[],
): HttpError | undefined {
  if (!file.name.toLocaleLowerCase().endsWith(".xlsx")) {
    return refusal(
      415,
      "Unsupported file format. Please upload an Excel file using the provided template.",
    );
  }
  const required: readonly string[] = BULK_COLUMNS;
  if (columns.some((column) => !required.includes(column))) {
    return refusal(
      400,
      "Invalid Geofence template. Please use the provided template.",
    );
  }
  const missing = required.filter((column) => !columns.includes(column));
  if (missing.length > 0) {
    return refusal(
      400,
      `Invalid template. Missing required column${missing.length > 1 ? "s" : ""}: ${missing.join(", ")}.`,
    );
  }
  if (sheet.length === 0) {
    return refusal(
      400,
      "The uploaded file does not contain any Geofence records.",
    );
  }
  if (sheet.length > GEOFENCE_BULK_LIMITS.maxRecords) {
    return refusal(
      400,
      "The file exceeds the limit of 5,000 Geofences per file.",
    );
  }
  return undefined;
}

/** A cell as a number within the limits, else `null`: empty and non-numeric
 *  cells aren't numbers. */
function numberWithin(
  cell: number | string | null,
  min: number,
  max: number,
): number | null {
  if (cell === null || String(cell).trim() === "") return null;
  const value = Number(cell);
  return Number.isFinite(value) && value >= min && value <= max ? value : null;
}

/** Every row judged as the API would (Scenarios 7 to 12): a row with a bad value
 *  is invalid; a good one with a name in use, or used higher up, a duplicate. */
function checkSheet(
  sheet: readonly MockGeofenceSheetRow[],
  geofences: readonly Geofence[],
): GeofenceBulkRow[] {
  const saved = new Set(
    geofences.map((geofence) => geofence.name.toLocaleLowerCase()),
  );
  const firstRows = new Map<string, number>();
  return sheet.map((row) => {
    const errors: string[] = [];
    const name = row.name?.trim() ?? "";
    if (name === "") errors.push("Geofence Name is required.");
    if (numberWithin(row.latitude, -90, 90) === null) {
      errors.push("lat is not valid.");
    }
    if (numberWithin(row.longitude, -180, 180) === null) {
      errors.push("lng is not valid.");
    }
    if (
      numberWithin(row.radius, GEOFENCE_RADIUS_MIN, GEOFENCE_RADIUS_MAX) === null
    ) {
      errors.push("Radius must be between 1 and 50,000 meters.");
    }
    if (errors.length > 0) return { ...row, status: "INVALID", errors };

    const key = name.toLocaleLowerCase();
    if (saved.has(key)) {
      return {
        ...row,
        status: "DUPLICATE",
        errors: [
          `Row ${String(row.row)}: A Geofence with this name already exists.`,
        ],
      };
    }
    const first = firstRows.get(key);
    if (first !== undefined) {
      return {
        ...row,
        status: "DUPLICATE",
        errors: [
          `Row ${String(row.row)}: This name is already used on row ${String(first)} of the file.`,
        ],
      };
    }
    firstRows.set(key, row.row);
    return { ...row, status: "VALID", errors: [] };
  });
}

function circle(
  latitude: number,
  longitude: number,
  radiusMeters: number,
): GeofenceShape {
  return { kind: "CIRCLE", center: { latitude, longitude }, radiusMeters };
}

/** A four-sided area around a spot, about a kilometer across. */
function area(latitude: number, longitude: number): GeofenceShape {
  const step = 0.005;
  return {
    kind: "POLYGON",
    points: [
      { latitude: latitude + step, longitude: longitude - step },
      { latitude: latitude + step, longitude: longitude + step },
      { latitude: latitude - step, longitude: longitude + step * 1.4 },
      { latitude: latitude - step, longitude: longitude - step },
    ],
  };
}

function mockGeofence(
  index: number,
  name: string,
  color: GeofenceColor,
  shape: GeofenceShape,
): Geofence {
  return {
    id: `geofence-${String(index)}`,
    code: String(49233 + index),
    name,
    color,
    shape,
  };
}

/** The geofence screen's sample data: areas around Riyadh, where the map
 *  opens. */
export const MOCK_GEOFENCES: readonly Geofence[] = [
  mockGeofence(1, "منطقة عمل شمال الرياض", "PURPLE", area(24.84861, 46.66219)),
  mockGeofence(2, "منطقة خدمات العليا", "PINK", circle(24.69011, 46.68522, 800)),
  mockGeofence(3, "منطقة محظورة - المطار", "RED", area(24.95764, 46.69875)),
  mockGeofence(4, "منطقة حذر - الطريق الدائري", "GOLD", circle(24.77251, 46.73884, 500)),
  mockGeofence(5, "Al Malqa safe zone", "GREEN", area(24.80512, 46.61576)),
  mockGeofence(6, "Southern depot work zone", "BLUE", circle(24.57312, 46.75218, 1200)),
  mockGeofence(7, "منطقة عمل النخيل", "PURPLE", area(24.75645, 46.62703)),
  mockGeofence(8, "منطقة آمنة - الياسمين", "GREEN", circle(24.82493, 46.64087, 600)),
  mockGeofence(9, "Exit 9 caution zone", "ORANGE", area(24.73418, 46.78852)),
  mockGeofence(10, "منطقة خدمات الملز", "GRAY", circle(24.65729, 46.71812, 700)),
  mockGeofence(11, "منطقة عمل الدرعية", "BLUE", area(24.73801, 46.57294)),
];

/** What the mock reads any uploaded `.xlsx` as: a bit of everything the check
 *  tells apart. */
const MOCK_BULK_SHEET: readonly MockGeofenceSheetRow[] = [
  { row: 2, name: "منطقة خدمات حطين", latitude: 24.76321, longitude: 46.60114, radius: 750 },
  { row: 3, name: "King Fahd road zone", latitude: 24.72845, longitude: 46.67092, radius: 1000 },
  { row: 4, name: "منطقة مستودع السلي", latitude: 24.67213, longitude: 46.84571, radius: 1500 },
  { row: 5, name: "منطقة خدمات العليا", latitude: 24.69011, longitude: 46.68522, radius: 800 },
  { row: 6, name: "", latitude: 24.70112, longitude: 46.71203, radius: 400 },
  { row: 7, name: "Out of range zone", latitude: 124.5, longitude: 46.71203, radius: 400 },
  { row: 8, name: "Bad longitude zone", latitude: 24.70112, longitude: "east", radius: 400 },
  { row: 9, name: "Too wide zone", latitude: 24.70112, longitude: 46.71203, radius: 60000 },
  { row: 10, name: "king fahd road zone", latitude: 24.73, longitude: 46.68, radius: 300 },
];

/** Geofences kept in memory, until the API has them, with one rule a form can
 *  trip over: a name is unique within the company, regardless of case. */
export function createMockGeofencesRepository(
  options: MockGeofencesRepositoryOptions = {},
): GeofencesRepository {
  const geofences: Geofence[] = [...(options.geofences ?? [])];
  const sheet = options.bulkSheet ?? MOCK_BULK_SHEET;
  const columns = options.bulkColumns ?? BULK_COLUMNS;
  const latency = options.latencyMs ?? 0;
  const wait = async () => {
    if (latency > 0)
      await new Promise((resolve) => setTimeout(resolve, latency));
  };
  let next = geofences.length;
  const nameTaken = (name: string, exceptId?: string) => {
    const taken = name.toLocaleLowerCase();
    return geofences.some(
      (kept) =>
        kept.id !== exceptId && kept.name.toLocaleLowerCase() === taken,
    );
  };
  return {
    async list(query) {
      await wait();
      // Newest first, as the POI list.
      const rows = matching(geofences, query.search).reverse();
      return paginateLocally(rows, query.page, query.limit);
    },
    async listForMap(query) {
      await wait();
      return matching(geofences, query.search).sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    },
    async getById(id) {
      await wait();
      const geofence = geofences.find((kept) => kept.id === id);
      if (geofence === undefined) throw notFound(id, "GET");
      return geofence;
    },
    async create(input) {
      await wait();
      const name = input.name.trim();
      if (nameTaken(name)) throw conflict("POST");
      next += 1;
      const geofence: Geofence = {
        id: `geofence-${String(next)}`,
        code: String(49233 + next),
        name,
        color: input.color,
        shape: input.shape,
      };
      geofences.push(geofence);
      return { geofence };
    },
    async downloadTemplate() {
      await wait();
      // Not a real workbook: stands in for the API's .xlsx.
      return new Blob([`${BULK_COLUMNS.join()}\n`], { type: SPREADSHEET_TYPE });
    },
    async exportGeofences(query) {
      await wait();
      // Not a real workbook: stands in for the API's .xlsx, newest first. A
      // polygon has no center or radius, so only its name fills a row.
      const rows = matching(geofences, query.search)
        .reverse()
        .map((geofence) =>
          (geofence.shape.kind === "CIRCLE"
            ? [
                geofence.name,
                geofence.shape.center.latitude,
                geofence.shape.center.longitude,
                geofence.shape.radiusMeters,
              ]
            : [geofence.name, "", "", ""]
          ).join(),
        );
      return new Blob([[BULK_COLUMNS.join(), ...rows].join("\n")], {
        type: SPREADSHEET_TYPE,
      });
    },
    async previewBulkUpload(file) {
      await wait();
      const refused = bulkFileRefusal(file, columns, sheet);
      if (refused !== undefined) throw refused;
      const rows = checkSheet(sheet, geofences);
      const count = (status: GeofenceBulkRow["status"]) =>
        rows.filter((row) => row.status === status).length;
      return {
        totalRecords: rows.length,
        validRecords: count("VALID"),
        invalidRecords: count("INVALID"),
        duplicateRecords: count("DUPLICATE"),
        rows,
      };
    },
    async bulkUpload(file) {
      await wait();
      const refused = bulkFileRefusal(file, columns, sheet);
      if (refused !== undefined) throw refused;
      const rows = checkSheet(sheet, geofences);
      const failedRows = rows.filter((row) => row.status !== "VALID");
      const valid = rows.filter((row) => row.status === "VALID");
      for (const row of valid) {
        next += 1;
        geofences.push({
          id: `geofence-${String(next)}`,
          code: String(49233 + next),
          name: row.name?.trim() ?? "",
          // The template has no color column.
          color: DEFAULT_GEOFENCE_COLOR,
          shape: circle(
            Number(row.latitude),
            Number(row.longitude),
            Number(row.radius),
          ),
        });
      }
      return {
        totalRecords: rows.length,
        createdRecords: valid.length,
        failedRecords: failedRows.length,
        failedRows,
      };
    },
    async downloadBulkErrorReport(file) {
      await wait();
      const refused = bulkFileRefusal(file, columns, sheet);
      if (refused !== undefined) throw refused;
      // Not a real workbook: stands in for the API's .xlsx of refused rows.
      const refusedRows = checkSheet(sheet, geofences)
        .filter((row) => row.status !== "VALID")
        .map((row) =>
          [
            row.row,
            row.name ?? "",
            row.latitude ?? "",
            row.longitude ?? "",
            row.radius ?? "",
            row.status,
            row.errors.join(" / "),
          ].join(),
        );
      return new Blob(
        [["row,name,lat,lng,radius,status,error", ...refusedRows].join("\n")],
        { type: SPREADSHEET_TYPE },
      );
    },
    async update(id, input) {
      await wait();
      const index = geofences.findIndex((kept) => kept.id === id);
      const current = geofences[index];
      if (current === undefined) throw notFound(id, "PUT");
      const name = input.name.trim();
      if (nameTaken(name, id)) throw conflict("PUT");
      const geofence: Geofence = {
        ...current,
        name,
        color: input.color,
        shape: input.shape,
      };
      geofences[index] = geofence;
      return { geofence };
    },
    async delete(id) {
      await wait();
      const index = geofences.findIndex((geofence) => geofence.id === id);
      if (index === -1) throw notFound(id, "DELETE");
      geofences.splice(index, 1);
      return {};
    },
  };
}
